import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowDown, ArrowUp, Eye, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ALLERGENS_14, DIETARY_LABELS, inputClass, labelClass } from "@/lib/constants";
import { Checkbox } from "@/components/ui/checkbox";

const emptyForm = {
  name: "", slug: "", category_slug: "", short_description: "", full_description: "",
  price: "", sale_price: "", option_names: [], variants: [],
  track_variant_stock: false, stock_quantity: "", availability_start: "", availability_end: "",
  sold_out: false, featured: false, active: false, ingredients: "", allergens: [],
  allergen_declaration_status: "unreviewed", may_contain: [],
  dietary_labels: [], storage_instructions: "", shelf_life: "", reheat_instructions: "",
  min_order_qty: 1, max_qty_per_customer: "", demo_content: true,
  owner_review_flags: [], additive_warnings: [],
};

const toPence = (v) => (v === "" || v == null ? null : Math.round(parseFloat(v) * 100));
const toPounds = (p) => (p == null ? "" : (p / 100).toFixed(2));

export default function AdminProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [form, setForm] = useState(emptyForm);
  const [images, setImages] = useState([]);
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [newOption, setNewOption] = useState("");
  const [newFlag, setNewFlag] = useState("");
  const [newAdditive, setNewAdditive] = useState("");
  const [baseUpdatedAt, setBaseUpdatedAt] = useState(null);
  const [recipeCheck, setRecipeCheck] = useState(false);

  useEffect(() => {
    api.get("/admin/categories").then((r) => setCategories(r.data));
    if (!isNew) {
      api.get(`/admin/products/${id}`).then((r) => {
        const p = r.data;
        setForm({
          ...emptyForm,
          ...p,
          price: toPounds(p.price_pence),
          sale_price: toPounds(p.sale_price_pence),
          stock_quantity: p.stock_quantity == null ? "" : String(p.stock_quantity),
          availability_start: p.availability_start || "",
          availability_end: p.availability_end || "",
          max_qty_per_customer: p.max_qty_per_customer == null ? "" : String(p.max_qty_per_customer),
          variants: (p.variants || []).map((v) => ({ ...v, price: toPounds(v.price_pence), stock: v.stock_quantity == null ? "" : String(v.stock_quantity) })),
          ingredients: p.ingredients || "",
          // legacy products without an explicit declaration derive safely: a non-empty
          // allergen list means "contains"; anything else stays "unreviewed"
          allergen_declaration_status: p.allergen_declaration_status || (p.allergens?.length ? "contains_regulated_allergens" : "unreviewed"),
          may_contain: p.may_contain || [],
        });
        setImages(p.images || []);
        setBaseUpdatedAt(p.updated_at || null);
      }).catch(() => navigate("/admin/products"));
    }
  }, [id, isNew, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));
  const toggle = (k) => (v) => setForm((f) => ({ ...f, [k]: v === true }));
  const toggleList = (k, value) => setForm((f) => ({ ...f, [k]: f[k].includes(value) ? f[k].filter((x) => x !== value) : [...f[k], value] }));
  // value-driven setter for checkboxes (robust against rapid successive clicks)
  const setListMember = (k, value, on) => setForm((f) => ({ ...f, [k]: on ? [...new Set([...f[k], value])] : f[k].filter((x) => x !== value) }));

  const addOptionName = () => {
    const name = newOption.trim();
    if (name && !form.option_names.includes(name)) setForm((f) => ({ ...f, option_names: [...f.option_names, name] }));
    setNewOption("");
  };

  const addVariant = () => setForm((f) => ({ ...f, variants: [...f.variants, { variant_id: null, sku: "", option_values: {}, price: "", active: true, stock: "" }] }));
  const setVariant = (i, patch) => setForm((f) => ({ ...f, variants: f.variants.map((v, j) => (j === i ? { ...v, ...patch } : v)) }));
  const removeVariant = (i) => setForm((f) => ({ ...f, variants: f.variants.filter((_, j) => j !== i) }));

  const save = async (activate = null) => {
    setError("");
    setBusy(true);
    const payload = {
      ...form,
      active: activate == null ? form.active : activate,
      price_pence: toPence(form.price),
      sale_price_pence: toPence(form.sale_price),
      stock_quantity: form.stock_quantity === "" ? null : parseInt(form.stock_quantity, 10),
      availability_start: form.availability_start || null,
      availability_end: form.availability_end || null,
      max_qty_per_customer: form.max_qty_per_customer === "" ? null : parseInt(form.max_qty_per_customer, 10),
      min_order_qty: parseInt(form.min_order_qty, 10) || 1,
      slug: form.slug || null,
      variants: form.variants.map((v) => ({
        variant_id: v.variant_id || null,
        sku: v.sku || null,
        option_values: v.option_values,
        price_pence: toPence(v.price),
        price_delta_pence: 0,
        active: v.active,
        stock_quantity: v.stock === "" ? null : parseInt(v.stock, 10),
      })),
    };
    ["price", "sale_price", "stock", "image"].forEach((k) => delete payload[k]);
    if (!isNew && baseUpdatedAt) payload.base_updated_at = baseUpdatedAt;
    try {
      if (isNew) {
        const res = await api.post("/admin/products", payload);
        toast.success("Product created");
        navigate(`/admin/products/${res.data.id}`);
      } else {
        await api.put(`/admin/products/${id}`, payload);
        const fresh = await api.get(`/admin/products/${id}`);
        setBaseUpdatedAt(fresh.data.updated_at || null);
        // re-sync server-computed food-safety state: edits to the declaration,
        // allergen list, may-contain or ingredients reset verification server-side
        setForm((f) => ({
          ...f,
          allergen_information_verified: fresh.data.allergen_information_verified,
          ingredient_information_verified: fresh.data.ingredient_information_verified,
          allergen_verified_by: fresh.data.allergen_verified_by,
          ingredient_verified_by: fresh.data.ingredient_verified_by,
          allergen_declaration_status: fresh.data.allergen_declaration_status || f.allergen_declaration_status,
          may_contain: fresh.data.may_contain || [],
        }));
        setRecipeCheck(false);
        toast.success("Product saved");
        if (activate !== null) setForm((f) => ({ ...f, active: activate }));
      }
    } catch (e) {
      if (e?.response?.status === 409) {
        setError("This product was changed in another tab or by someone else — your changes were NOT saved. Refresh the page to see the latest version, then make your changes again.");
      } else {
        setError(formatApiError(e));
      }
      window.scrollTo({ top: 0 });
    } finally {
      setBusy(false);
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const data = new FormData();
    data.append("file", file);
    data.append("alt", form.name ? `${form.name} — product photo` : "Product photo");
    try {
      const res = await api.post(`/admin/products/${id}/images`, data);
      setImages((imgs) => [...imgs, res.data]);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    e.target.value = "";
  };

  const moveImage = async (i, dir) => {
    const next = [...images];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setImages(next);
    await api.post(`/admin/products/${id}/images/reorder`, { image_ids: next.map((x) => x.image_id) }).catch(() => {});
  };

  const deleteImage = async (imageId) => {
    await api.delete(`/admin/products/${id}/images/${imageId}`).catch((e) => toast.error(formatApiError(e)));
    setImages((imgs) => imgs.filter((x) => x.image_id !== imageId));
  };

  const updateAlt = async (imageId, alt) => {
    setImages((imgs) => imgs.map((x) => (x.image_id === imageId ? { ...x, alt } : x)));
    await api.put(`/admin/products/${id}/images/${imageId}`, { alt }).catch(() => {});
  };

  return (
    <div data-testid="admin-product-edit-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/admin/products" className="text-xs font-bold text-gold hover:underline" data-testid="back-to-products-link">← Products</Link>
          <h1 className="mt-1 font-serif text-2xl font-bold text-cream-text">{isNew ? "New product" : `Edit: ${form.name}`}</h1>
        </div>
        {!isNew && (
          <Link to={`/admin/products/${id}/preview`} className="inline-flex items-center gap-2 rounded-full border border-choco-line px-5 py-2.5 text-sm font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="preview-product-button">
            <Eye className="h-4 w-4" aria-hidden="true" /> Preview
          </Link>
        )}
      </div>

      {error && <p className="mt-4 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 text-sm font-semibold text-red-300" role="alert" data-testid="product-save-error">{error}</p>}

      <div className="mt-6 space-y-6">
        <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="section-basics">
          <h2 className="font-serif text-lg font-semibold text-cream-text">Basics</h2>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="p-name" className={labelClass}>Name *</label>
              <input id="p-name" value={form.name} onChange={set("name")} className={inputClass} data-testid="product-name-input" />
            </div>
            <div>
              <label htmlFor="p-slug" className={labelClass}>URL slug (optional)</label>
              <input id="p-slug" value={form.slug} onChange={set("slug")} placeholder="auto-generated from name" className={inputClass} data-testid="product-slug-input" />
            </div>
            <div>
              <label htmlFor="p-category" className={labelClass}>Category *</label>
              <select id="p-category" value={form.category_slug} onChange={set("category_slug")} className={inputClass} data-testid="product-category-select">
                <option value="">Choose…</option>
                {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-6 pb-1">
              {[
                ["active", "Live (visible in shop)", "product-active-toggle"],
                ["featured", "Featured", "product-featured-toggle"],
                ["sold_out", "Sold out", "product-soldout-toggle"],
              ].map(([k, label, testid]) => (
                <div key={k} className="flex items-center gap-2">
                  <Checkbox id={testid} checked={form[k]} onCheckedChange={toggle(k)} className="border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco" data-testid={testid} />
                  <label htmlFor={testid} className="text-sm text-cream-muted">{label}</label>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <label htmlFor="p-short" className={labelClass}>Short description</label>
            <input id="p-short" value={form.short_description} onChange={set("short_description")} className={inputClass} data-testid="product-short-desc-input" />
          </div>
          <div className="mt-4">
            <label htmlFor="p-full" className={labelClass}>Full description</label>
            <textarea id="p-full" rows={4} value={form.full_description} onChange={set("full_description")} className={inputClass} data-testid="product-full-desc-input" />
          </div>
        </section>

        <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="section-pricing">
          <h2 className="font-serif text-lg font-semibold text-cream-text">Pricing</h2>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <label htmlFor="p-price" className={labelClass}>Price (£) *</label>
              <input id="p-price" type="number" step="0.01" min="0" value={form.price} onChange={set("price")} className={inputClass} data-testid="product-price-input" />
            </div>
            <div>
              <label htmlFor="p-sale" className={labelClass}>Sale price (£)</label>
              <input id="p-sale" type="number" step="0.01" min="0" value={form.sale_price} onChange={set("sale_price")} className={inputClass} data-testid="product-sale-price-input" />
            </div>
            <div>
              <label htmlFor="p-min" className={labelClass}>Min order qty</label>
              <input id="p-min" type="number" min="1" value={form.min_order_qty} onChange={set("min_order_qty")} className={inputClass} data-testid="product-min-qty-input" />
            </div>
            <div>
              <label htmlFor="p-max" className={labelClass}>Max per customer</label>
              <input id="p-max" type="number" min="1" value={form.max_qty_per_customer} onChange={set("max_qty_per_customer")} className={inputClass} data-testid="product-max-qty-input" />
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="section-variations">
          <h2 className="font-serif text-lg font-semibold text-cream-text">Options &amp; variations</h2>
          <div className="mt-4 flex flex-wrap items-end gap-3">
            <div>
              <label htmlFor="p-new-option" className={labelClass}>Option name (e.g. Box size, Flavour)</label>
              <input id="p-new-option" value={newOption} onChange={(e) => setNewOption(e.target.value)} className={inputClass} data-testid="new-option-name-input" />
            </div>
            <button type="button" onClick={addOptionName} className="rounded-full border border-choco-line px-4 py-2.5 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="add-option-name-button">Add option</button>
          </div>
          {form.option_names.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {form.option_names.map((n) => (
                <span key={n} className="inline-flex items-center gap-2 rounded-full bg-choco px-3 py-1 text-xs font-bold text-cream-text">
                  {n}
                  <button type="button" aria-label={`Remove option ${n}`} onClick={() => setForm((f) => ({ ...f, option_names: f.option_names.filter((x) => x !== n) }))} className="text-cream-muted hover:text-red-400">×</button>
                </span>
              ))}
            </div>
          )}
          <div className="mt-5 space-y-3" data-testid="variants-list">
            {form.variants.map((v, i) => (
              <div key={i} className="flex flex-wrap items-end gap-3 rounded-2xl border border-choco-line bg-choco p-4" data-testid={`variant-row-${i}`}>
                {form.option_names.map((n) => (
                  <div key={n}>
                    <label className="mb-1 block text-xs font-semibold text-cream-muted">{n}</label>
                    <input value={v.option_values[n] || ""} onChange={(e) => setVariant(i, { option_values: { ...v.option_values, [n]: e.target.value } })} className={`${inputClass} w-36`} data-testid={`variant-${i}-option-${n.toLowerCase().replace(/\s+/g, "-")}`} />
                  </div>
                ))}
                <div>
                  <label className="mb-1 block text-xs font-semibold text-cream-muted">Price (£)</label>
                  <input type="number" step="0.01" min="0" value={v.price} onChange={(e) => setVariant(i, { price: e.target.value })} className={`${inputClass} w-28`} data-testid={`variant-${i}-price`} />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-cream-muted">SKU</label>
                  <input value={v.sku || ""} onChange={(e) => setVariant(i, { sku: e.target.value })} className={`${inputClass} w-28`} data-testid={`variant-${i}-sku`} />
                </div>
                {form.track_variant_stock && (
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-cream-muted">Stock</label>
                    <input type="number" min="0" value={v.stock} onChange={(e) => setVariant(i, { stock: e.target.value })} className={`${inputClass} w-24`} data-testid={`variant-${i}-stock`} />
                  </div>
                )}
                <div className="flex items-center gap-2 pb-2">
                  <Checkbox id={`variant-active-${i}`} checked={v.active} onCheckedChange={(val) => setVariant(i, { active: val === true })} className="border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco" data-testid={`variant-${i}-active`} />
                  <label htmlFor={`variant-active-${i}`} className="text-xs text-cream-muted">Active</label>
                </div>
                <button type="button" onClick={() => removeVariant(i)} aria-label={`Remove variation ${i + 1}`} className="rounded-full p-2 text-cream-muted hover:text-red-400" data-testid={`variant-${i}-remove`}>
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
          {form.option_names.length > 0 && (
            <button type="button" onClick={addVariant} className="mt-4 rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="add-variant-button">
              + Add variation
            </button>
          )}
        </section>

        <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="section-inventory">
          <h2 className="font-serif text-lg font-semibold text-cream-text">Inventory &amp; availability</h2>
          <div className="mt-4 flex items-center gap-2">
            <Checkbox id="p-track-variant" checked={form.track_variant_stock} onCheckedChange={toggle("track_variant_stock")} className="border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco" data-testid="track-variant-stock-toggle" />
            <label htmlFor="p-track-variant" className="text-sm text-cream-muted">Track stock per variation (instead of product level)</label>
          </div>
          {!form.track_variant_stock && (
            <div className="mt-4 max-w-xs">
              <label htmlFor="p-stock" className={labelClass}>Stock quantity (blank = unlimited)</label>
              <input id="p-stock" type="number" min="0" value={form.stock_quantity} onChange={set("stock_quantity")} className={inputClass} data-testid="product-stock-input" />
            </div>
          )}
          <div className="mt-4 grid grid-cols-2 gap-4 sm:max-w-md">
            <div>
              <label htmlFor="p-avail-start" className={labelClass}>Available from</label>
              <input id="p-avail-start" type="date" value={form.availability_start} onChange={set("availability_start")} className={inputClass} data-testid="availability-start-input" />
            </div>
            <div>
              <label htmlFor="p-avail-end" className={labelClass}>Available until</label>
              <input id="p-avail-end" type="date" value={form.availability_end} onChange={set("availability_end")} className={inputClass} data-testid="availability-end-input" />
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="section-food-info">
          <h2 className="font-serif text-lg font-semibold text-cream-text">Ingredients &amp; allergens</h2>
          <div className="mt-4">
            <label htmlFor="p-ingredients" className={labelClass}>Ingredients</label>
            <textarea id="p-ingredients" rows={3} value={form.ingredients} onChange={set("ingredients")} className={inputClass} data-testid="product-ingredients-input" />
          </div>
          <fieldset className="mt-5">
            <legend className="text-sm font-semibold text-cream-text">Allergen declaration (required before this bake can be published or ordered)</legend>
            <div className="mt-2 space-y-2" data-testid="allergen-declaration-radios">
              {[
                ["contains_regulated_allergens", "Contains regulated allergens", "Tick every UK 14 regulated allergen used as an ingredient in the list that appears below.", "declaration-contains"],
                ["none_of_14_as_ingredients", "None of the UK 14 regulated allergens are used as ingredients", "Choose this only after checking the complete recipe, compound ingredients, supplier labels, additives and processing aids. Never describe the bake as “allergen-free” or “safe for allergy sufferers”.", "declaration-none"],
                ["unreviewed", "Not reviewed yet", "The safe default — the bake cannot be published or ordered while this is selected.", "declaration-unreviewed"],
              ].map(([value, label, hint, testid]) => (
                <label key={value} className={`flex cursor-pointer items-start gap-3 rounded-2xl border p-3 transition-colors ${form.allergen_declaration_status === value ? "border-gold bg-choco" : "border-choco-line hover:border-gold/50"}`} data-testid={testid}>
                  <input type="radio" name="allergen_declaration" value={value} className="mt-1 accent-gold"
                    checked={form.allergen_declaration_status === value}
                    onChange={() => setForm((f) => ({
                      ...f,
                      allergen_declaration_status: value,
                      // mutual exclusivity: "none of the UK 14" cannot carry declared allergens
                      allergens: value === "none_of_14_as_ingredients" ? [] : f.allergens,
                    }))}
                    data-testid={`${testid}-input`} />
                  <span>
                    <span className="block text-sm font-semibold text-cream-text">{label}</span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-cream-muted">{hint}</span>
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-2 text-xs text-cream-muted">Changing the declaration, the allergen list or the may-contain list resets verification — you will need to verify again after saving.</p>
          </fieldset>
          {form.allergen_declaration_status === "contains_regulated_allergens" && (
            <fieldset className="mt-4">
              <legend className="text-sm font-semibold text-cream-text">Contains (regulated allergens used as ingredients)</legend>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="allergen-checkboxes">
                {ALLERGENS_14.map((a) => (
                  <div key={a} className="flex items-center gap-2">
                    <Checkbox id={`allergen-${a}`} checked={form.allergens.includes(a)} onCheckedChange={(c) => setListMember("allergens", a, c === true)} className="border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco" data-testid={`allergen-${a.replace(/\s+/g, "-")}`} />
                    <label htmlFor={`allergen-${a}`} className="text-xs capitalize text-cream-muted">{a}</label>
                  </div>
                ))}
              </div>
            </fieldset>
          )}
          <fieldset className="mt-5">
            <legend className="text-sm font-semibold text-cream-text">May contain / cross-contamination risk (optional)</legend>
            <p className="mt-1 text-xs leading-relaxed text-cream-muted">
              Precautionary information about the preparation kitchen only — never ingredient allergens, and never generated
              automatically. Enter it only after your own risk assessment of how the bake is prepared, stored and packed.
              Do not repeat anything already declared as an ingredient above.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="may-contain-checkboxes">
              {ALLERGENS_14.map((a) => (
                <div key={a} className="flex items-center gap-2">
                  <Checkbox id={`may-${a}`} checked={form.may_contain.includes(a)} disabled={form.allergens.includes(a)} onCheckedChange={(c) => setListMember("may_contain", a, c === true)} className="border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco disabled:opacity-40" data-testid={`may-contain-${a.replace(/\s+/g, "-")}`} />
                  <label htmlFor={`may-${a}`} className="text-xs capitalize text-cream-muted">{a}</label>
                </div>
              ))}
            </div>
          </fieldset>
          <fieldset className="mt-5">
            <legend className="text-sm font-semibold text-cream-text">Dietary labels (set only when you're certain)</legend>
            <div className="mt-2 flex flex-wrap gap-4" data-testid="dietary-label-checkboxes">
              {DIETARY_LABELS.map((d) => (
                <div key={d} className="flex items-center gap-2">
                  <Checkbox id={`diet-${d}`} checked={form.dietary_labels.includes(d)} onCheckedChange={() => toggleList("dietary_labels", d)} className="border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco" data-testid={`dietary-${d}`} />
                  <label htmlFor={`diet-${d}`} className="text-xs capitalize text-cream-muted">{d}</label>
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-cream-muted">Labels are never inferred from ingredients — only set them when you are confident in the claim.</p>
          </fieldset>
          <div className="mt-5 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="owner-review-flags-section">
            <legend className="text-sm font-semibold text-amber-300">Owner review flags — confirm each against the recipe, then remove it</legend>
            {form.owner_review_flags.length === 0 ? (
              <p className="mt-2 text-xs text-cream-muted" data-testid="no-review-flags">No outstanding review flags.</p>
            ) : (
              <ul className="mt-2 space-y-2" data-testid="review-flags-list">
                {form.owner_review_flags.map((f, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 rounded-xl bg-choco p-3 text-xs leading-relaxed text-cream-text" data-testid={`review-flag-${i}`}>
                    <span>{f}</span>
                    <button type="button" onClick={() => setForm((fm) => ({ ...fm, owner_review_flags: fm.owner_review_flags.filter((_, j) => j !== i) }))} aria-label={`Remove review flag ${i + 1}`} className="shrink-0 rounded-full border border-choco-line px-2.5 py-1 text-[11px] font-bold text-cream-muted hover:border-gold hover:text-gold" data-testid={`remove-flag-${i}`}>
                      Confirmed — remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-3 flex gap-2">
              <input value={newFlag} onChange={(e) => setNewFlag(e.target.value)} placeholder="Add a note that needs your confirmation" aria-label="New review flag" className={`${inputClass} flex-1`} data-testid="new-flag-input" />
              <button type="button" onClick={() => { const v = newFlag.trim(); if (v) setForm((fm) => ({ ...fm, owner_review_flags: [...fm.owner_review_flags, v] })); setNewFlag(""); }} className="rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="add-flag-button">Add</button>
            </div>
          </div>
          <div className="mt-5">
            <legend className="text-sm font-semibold text-cream-text">Additive / food-colour warnings (e.g. E129 — shown to customers, not allergens)</legend>
            {form.additive_warnings.length > 0 && (
              <ul className="mt-2 space-y-2" data-testid="additive-warnings-list">
                {form.additive_warnings.map((w, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 rounded-xl bg-choco p-3 text-xs leading-relaxed text-cream-text" data-testid={`additive-warning-row-${i}`}>
                    <span>{w}</span>
                    <button type="button" onClick={() => setForm((fm) => ({ ...fm, additive_warnings: fm.additive_warnings.filter((_, j) => j !== i) }))} aria-label={`Remove additive warning ${i + 1}`} className="shrink-0 text-cream-muted hover:text-red-400" data-testid={`remove-additive-${i}`}>×</button>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-2 flex gap-2">
              <input value={newAdditive} onChange={(e) => setNewAdditive(e.target.value)} placeholder="e.g. Contains E129 — may affect activity and attention in children" aria-label="New additive warning" className={`${inputClass} flex-1`} data-testid="new-additive-input" />
              <button type="button" onClick={() => { const v = newAdditive.trim(); if (v) setForm((fm) => ({ ...fm, additive_warnings: [...fm.additive_warnings, v] })); setNewAdditive(""); }} className="rounded-full border border-choco-line px-4 py-2 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="add-additive-button">Add</button>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label htmlFor="p-storage" className={labelClass}>Storage</label>
              <input id="p-storage" value={form.storage_instructions} onChange={set("storage_instructions")} className={inputClass} data-testid="product-storage-input" />
            </div>
            <div>
              <label htmlFor="p-shelf" className={labelClass}>Shelf life</label>
              <input id="p-shelf" value={form.shelf_life} onChange={set("shelf_life")} className={inputClass} data-testid="product-shelf-life-input" />
            </div>
            <div>
              <label htmlFor="p-reheat" className={labelClass}>Heating / baking</label>
              <input id="p-reheat" value={form.reheat_instructions} onChange={set("reheat_instructions")} className={inputClass} data-testid="product-reheat-input" />
            </div>
          </div>
        </section>

        {!isNew && (
          <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="section-images">
            <h2 className="font-serif text-lg font-semibold text-cream-text">Images</h2>
            <p className="mt-1 text-xs text-cream-muted">JPEG, PNG or WebP, up to 8MB. Images are automatically optimised to three sizes.</p>
            <div className="mt-4 space-y-3" data-testid="images-list">
              {images.map((img, i) => (
                <div key={img.image_id} className="flex flex-wrap items-center gap-3 rounded-2xl border border-choco-line bg-choco p-3" data-testid={`image-row-${i}`}>
                  <img src={img.url_400 || img.url} alt={img.alt} className="h-16 w-16 rounded-xl object-cover" />
                  <input value={img.alt} onChange={(e) => updateAlt(img.image_id, e.target.value)} placeholder="Descriptive alt text" aria-label="Image alt text" className={`${inputClass} min-w-40 flex-1`} data-testid={`image-alt-${i}`} />
                  {img.is_demo && <span className="rounded-full bg-amber-500/20 px-2.5 py-0.5 text-[11px] font-bold text-amber-300">Demo image</span>}
                  <div className="flex gap-1">
                    <button type="button" onClick={() => moveImage(i, -1)} aria-label="Move image up" className="rounded-full p-2 text-cream-muted hover:text-gold" data-testid={`image-up-${i}`}><ArrowUp className="h-4 w-4" /></button>
                    <button type="button" onClick={() => moveImage(i, 1)} aria-label="Move image down" className="rounded-full p-2 text-cream-muted hover:text-gold" data-testid={`image-down-${i}`}><ArrowDown className="h-4 w-4" /></button>
                    <button type="button" onClick={() => deleteImage(img.image_id)} aria-label="Remove image" className="rounded-full p-2 text-cream-muted hover:text-red-400" data-testid={`image-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
            </div>
            <label className="mt-4 inline-flex cursor-pointer items-center gap-2 rounded-full border border-choco-line px-5 py-2.5 text-xs font-bold text-cream-text hover:border-gold hover:text-gold" data-testid="upload-image-button">
              <Upload className="h-4 w-4" aria-hidden="true" /> Upload image
              <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="sr-only" data-testid="upload-image-input" />
            </label>
          </section>
        )}

        {!isNew && (
          <section className="rounded-3xl border border-choco-line bg-choco-light p-6" data-testid="section-food-safety">
            <h2 className="font-serif text-lg font-semibold text-cream-text">Food information verification</h2>
            <p className="mt-1 text-xs text-cream-muted">
              Verifying means you have personally checked this information against the actual recipe and how the product is prepared in your kitchen. It is not legal approval, and pressing the button does not check anything for you. Changing the allergen declaration, allergen list, may-contain list or ingredients resets verification automatically.
            </p>
            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[
                ["allergens", "allergen_information_verified", "Allergen declarations", "allergen"],
                ["ingredients", "ingredient_information_verified", "Ingredients list", "ingredient"],
              ].map(([kind, flag, title]) => (
                <div key={kind} className="rounded-2xl border border-choco-line bg-choco p-4" data-testid={`verify-${kind}-panel`}>
                  <p className="text-sm font-semibold text-cream-text">{title}</p>
                  <p className={`mt-1 text-xs font-bold ${form[flag] ? "text-green-400" : "text-amber-400"}`} data-testid={`verify-${kind}-status`}>
                    {form[flag] ? "Verified by owner" : "NOT VERIFIED"}
                  </p>
                  {form[flag] && form[`${kind === "allergens" ? "allergen" : "ingredient"}_verified_by`] && (
                    <p className="mt-1 text-[11px] text-cream-muted">
                      by {form[`${kind === "allergens" ? "allergen" : "ingredient"}_verified_by`]} — the exact wording verified is kept in the change history
                    </p>
                  )}
                  {!form[flag] && kind === "allergens" && form.allergen_declaration_status === "none_of_14_as_ingredients" && (
                    <label className="mt-3 flex items-start gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs leading-relaxed text-cream-text" data-testid="full-recipe-check-label">
                      <Checkbox checked={recipeCheck} onCheckedChange={(v) => setRecipeCheck(v === true)} className="mt-0.5 border-choco-line data-[state=checked]:bg-gold data-[state=checked]:text-choco" data-testid="full-recipe-check-checkbox" />
                      I have checked the complete recipe, compound ingredients, supplier labels, additives and processing aids, and none of the UK 14 regulated allergens are used as ingredients.
                    </label>
                  )}
                  {!form[flag] && (
                    <button
                      type="button"
                      disabled={kind === "allergens" && form.allergen_declaration_status === "none_of_14_as_ingredients" && !recipeCheck}
                      onClick={async () => {
                        const isNoneDeclaration = kind === "allergens" && form.allergen_declaration_status === "none_of_14_as_ingredients";
                        const confirmText = isNoneDeclaration
                          ? "I confirm I have checked the complete recipe, compound ingredients, supplier labels, additives and processing aids, and none of the UK 14 regulated allergens are used as ingredients."
                          : `I confirm I have checked the ${title.toLowerCase()} above against the actual recipe and preparation process.`;
                        if (!window.confirm(confirmText)) return;
                        try {
                          await api.post(`/admin/products/${id}/verify-food-info`, { kind, confirm: true, confirm_full_recipe_check: isNoneDeclaration });
                          toast.success(`${title} verified`);
                          setForm((f) => ({ ...f, [flag]: true }));
                          setRecipeCheck(false);
                        } catch (e) {
                          toast.error(formatApiError(e));
                        }
                      }}
                      className="mt-3 rounded-full border border-gold px-4 py-2.5 text-xs font-bold text-gold hover:bg-gold hover:text-choco disabled:cursor-not-allowed disabled:opacity-40"
                      data-testid={`verify-${kind}-button`}
                    >
                      I have checked this against my recipe — verify
                    </button>
                  )}
                </div>
              ))}
            </div>
            <Link to={`/admin/products/${id}/label`} className="mt-4 inline-block text-xs font-bold text-gold hover:underline" data-testid="print-label-link">
              Print allergen &amp; ingredients label
            </Link>
          </section>
        )}

        <div className="sticky bottom-4 flex flex-wrap gap-3 rounded-full border border-choco-line bg-choco-light/95 px-5 py-3 backdrop-blur-md">
          <button type="button" disabled={busy} onClick={() => save()} className="rounded-full border border-choco-line px-6 py-2.5 text-sm font-bold text-cream-text hover:border-gold hover:text-gold disabled:opacity-50" data-testid="save-draft-button">
            {busy ? "Saving…" : "Save"}
          </button>
          <button type="button" disabled={busy} onClick={() => save(true)} className="rounded-full bg-gold px-6 py-2.5 text-sm font-bold text-choco hover:bg-gold-hover disabled:opacity-50" data-testid="publish-button">
            {busy ? "Saving…" : form.active ? "Save (live)" : "Save & publish"}
          </button>
        </div>
      </div>
    </div>
  );
}
